/**
 * features/finance/reconFixWrite.ts
 * ------------------------------------------------------------------
 * FINANCE — the writes behind the Pastel Check "fix" cards
 * (lib/reconFixes decides which fix fits; this carries it out).
 * HIGH-RISK-CHANGES.md §8.
 *
 * Each action goes the way the rest of the portal already goes, so the
 * ledger, receipts and audit trail cannot tell a fix from ordinary work:
 *
 *   recordReconPayment  as Payments.tsx: applyPayment, receipt number,
 *                       payment.capture audit
 *   reverseReconPayment as ReversePaymentPanel: a negative isReversal entry,
 *                       the original flagged reversed, the invoice's paid /
 *                       balance / status restored (credit notes kept in the
 *                       sum), payment.reverse audit. For a portal payment
 *                       Pastel has no receipt for (Oct 2026).
 *   raiseReconInvoice   as ManualInvoice.tsx, minus the email: a
 *                       reconciliation charge is not news for a parent
 *                       until the bursar sends it. Carries kind:'invoice'.
 *   voidInvoice         NEW — nothing in the portal voided before. Only an
 *                       invoice with nothing paid or credited on it, never
 *                       an opening balance. Fields: status 'void' plus
 *                       voidedAt / voidedBy / voidedByName / voidReason.
 *                       Every balance reader already skips a void invoice,
 *                       and the GL trigger reverses one once posting is on.
 *   linkPastelCode /    reportStudents: pastelCode or fullName only — the
 *   renamePupil         one change finance managers may make to a pupil
 *                       (firestore.rules).
 *   noteDifference      financeReconNotes: "the portal is right, here is
 *                       why" — hides the card until the difference moves.
 *
 *   logReconChange /    financeReconChanges (lib/reconChanges): every reversal
 *   undoReconChange     or void made to match Pastel, and the way back — a
 *                       reversal undone by writing the payment again (the
 *                       original stays reversed: a ledger, not an eraser),
 *                       a void undone by putting the status back
 *                       (HIGH-RISK-CHANGES.md §13).
 *
 * Every action is audited with source 'pastel-recon' and the Pastel file.
 * ------------------------------------------------------------------ */
import { collection, doc } from 'firebase/firestore';
import { addDoc, db, setDoc, updateDoc } from '@/lib/firebase';
import { RECON_CHANGES_COLLECTION, type ReconChange } from '@/lib/reconChanges';
import { finance } from '@/features/finance/financeWrite';
import { recordFinanceAudit } from '@/lib/financeAudit';
import {
  applyPayment, assembleInvoice, formatInvoiceNo, formatReceiptNo, round2,
  invoiceStatus, type FeeCategory, type FinanceSettings, type Invoice, type Payment, type PaymentMethod,
} from '@/lib/financeCore';
import type { TermKey } from '@/lib/school.config';
import { voidRefusal } from '@/lib/reconFixes';

export const RECON_NOTES_COLLECTION = 'financeReconNotes';

export interface ReconActor {
  id: string;
  name: string;
  email?: string | null;
}

const audit = (action: string, actor: ReconActor, subjectId: string, before: unknown, after: unknown, file: string) =>
  recordFinanceAudit({
    action,
    actor: { id: actor.id, email: actor.email ?? null },
    subjectId,
    before: before as Record<string, unknown> | undefined,
    after: { ...(after as Record<string, unknown>), source: 'pastel-recon', pastelFile: file },
  });

export async function voidInvoice(inv: Invoice, reason: string, actor: ReconActor, file: string): Promise<void> {
  const refusal = voidRefusal(inv);
  if (refusal) throw new Error(refusal);
  if (reason.trim().length < 3) throw new Error('Give a reason for voiding it.');
  const now = new Date().toISOString();
  await finance.invoices.update(inv.id, {
    status: 'void', voidedAt: now, voidedBy: actor.id, voidedByName: actor.name, voidReason: reason.trim(),
  });
  await audit('invoice.void', actor, inv.id, { status: inv.status, total: inv.total }, { status: 'void', reason: reason.trim(), invoiceNo: inv.invoiceNo }, file);
}

export async function recordReconPayment(input: {
  invoice: Invoice;
  amount: number;
  date: string; // YYYY-MM-DD
  method: PaymentMethod;
  reference: string;
  note: string;
  /** Payments already recorded — the receipt sequence, as Payments.tsx counts it. */
  paymentCount: number;
  actor: ReconActor;
  file: string;
}): Promise<string> {
  const { invoice, actor } = input;
  const amt = round2(input.amount);
  if (!(amt > 0)) throw new Error('The amount must be more than nothing.');
  if (invoice.status === 'void') throw new Error('That invoice is void — choose another.');
  const receiptNo = formatReceiptNo(invoice.year, input.paymentCount + 1);
  const upd = applyPayment(invoice, amt);
  await finance.payments.add({
    invoiceId: invoice.id, studentId: invoice.studentId, studentName: invoice.studentName,
    familyId: invoice.familyId, amount: amt, method: input.method,
    reference: input.reference.trim() || undefined, receiptNo,
    receivedAt: new Date(`${input.date}T12:00:00`).toISOString(),
    recordedBy: actor.id, recordedByName: actor.name,
    note: input.note.trim() || undefined,
  });
  await finance.invoices.update(invoice.id, upd);
  await audit('payment.capture', actor, invoice.id, undefined, { amount: amt, method: input.method, receiptNo }, input.file);
  return receiptNo;
}

export async function reverseReconPayment(payment: Payment, invoice: Invoice | undefined, reason: string, actor: ReconActor, file: string): Promise<string> {
  if (payment.reversed || payment.isReversal) throw new Error('That payment is already reversed.');
  if (reason.trim().length < 3) throw new Error('Give a reason for reversing it.');
  const now = new Date().toISOString();
  // 1) The negative entry that offsets the original in every sum.
  const revId = await finance.payments.add({
    invoiceId: payment.invoiceId, studentId: payment.studentId, studentName: payment.studentName, familyId: payment.familyId,
    amount: round2(-payment.amount), method: payment.method,
    reference: `Reversal of ${payment.receiptNo ?? payment.reference ?? payment.id}`,
    receivedAt: now, recordedBy: actor.id, recordedByName: actor.name,
    note: reason.trim(), isReversal: true, reversalOf: payment.id,
  });
  // 2) The original, kept and flagged.
  await finance.payments.update(payment.id, {
    reversed: true, reversedAt: now, reversedBy: actor.id, reversedByName: actor.name, reversedReason: reason.trim(), reversalPaymentId: revId,
  });
  // 3) The invoice it was against, paid/balance/status restored.
  if (invoice) await finance.invoices.update(invoice.id, applyPayment(invoice, -payment.amount));
  await audit('payment.reverse', actor, payment.invoiceId,
    { paymentId: payment.id, amount: payment.amount, receiptNo: payment.receiptNo, recordedByName: payment.recordedByName },
    { reason: reason.trim(), reversalPaymentId: revId }, file);
  return revId;
}

/** Record a change made to match Pastel, so the second tab can show it and undo it. */
export async function logReconChange(change: Omit<ReconChange, 'id' | 'at' | 'byId' | 'byName' | 'status'>, actor: ReconActor): Promise<void> {
  await addDoc(collection(db, RECON_CHANGES_COLLECTION), {
    ...change, at: new Date().toISOString(), byId: actor.id, byName: actor.name, status: 'applied',
  });
}

/**
 * Put a change back. A reversal is undone by writing the payment again —
 * same amount, date, method and receipt number, against the same invoice —
 * because the reversal entry is history and stays. A void is undone by
 * putting the invoice's status back, worked out from what is on it now.
 */
export async function undoReconChange(
  change: ReconChange,
  ctx: { payments: Payment[]; invoices: Invoice[] },
  actor: ReconActor,
): Promise<void> {
  if (change.status !== 'applied') throw new Error('That change has already been undone.');
  const now = new Date().toISOString();
  const done: Record<string, unknown> = { status: 'undone', undoneAt: now, undoneById: actor.id, undoneByName: actor.name };
  if (change.kind === 'reversePayment') {
    const o = ctx.payments.find((p) => p.id === change.paymentId);
    if (!o) throw new Error('The original payment is no longer there.');
    const inv = ctx.invoices.find((i) => i.id === o.invoiceId);
    const newId = await finance.payments.add({
      invoiceId: o.invoiceId, studentId: o.studentId, studentName: o.studentName, familyId: o.familyId,
      amount: round2(o.amount), method: o.method, receiptNo: o.receiptNo,
      reference: `Reinstated ${o.receiptNo ?? o.reference ?? o.id}`,
      receivedAt: o.receivedAt, recordedBy: actor.id, recordedByName: actor.name,
      note: `Reversal undone — ${o.receiptNo ?? 'the payment'} stands`, reinstates: o.id,
    });
    if (inv) await finance.invoices.update(inv.id, applyPayment(inv, o.amount));
    done.reinstatedPaymentId = newId;
    await audit('payment.reinstate', actor, o.invoiceId, { paymentId: o.id, reversed: true }, { reinstatedPaymentId: newId, amount: o.amount, receiptNo: o.receiptNo }, change.pastelFile ?? '');
  } else if (change.kind === 'addPupil' || change.kind === 'linkPupil') {
    /* The Pastel code comes off the pupil, so the account shows as unmatched
       again; an opening balance added with them is voided. A pupil record
       added cannot be deleted from here — Admissions removes it if it
       should not exist. */
    if (change.kind === 'addPupil' && change.invoiceId) {
      const inv = ctx.invoices.find((i) => i.id === change.invoiceId);
      if (inv && inv.status !== 'void') {
        await finance.invoices.update(inv.id, { status: 'void', voidedAt: now, voidedBy: actor.id, voidedByName: actor.name, voidReason: 'Added from Pastel in error — undone' });
        await audit('invoice.void', actor, inv.id, { status: inv.status, total: inv.total }, { status: 'void', invoiceNo: inv.invoiceNo }, change.pastelFile ?? '');
      }
    }
    await updateDoc(doc(db, 'reportStudents', change.studentId), { pastelCode: '', updatedAt: now, updatedBy: actor.id });
    await audit('student.pastelCode', actor, change.studentId, { pastelCode: change.pastelCode }, { pastelCode: '' }, change.pastelFile ?? '');
  } else {
    const inv = ctx.invoices.find((i) => i.id === change.invoiceId);
    if (!inv) throw new Error('That invoice is no longer there.');
    if (inv.status !== 'void') throw new Error('That invoice is not void.');
    const status = invoiceStatus(inv.total, Number(inv.paid) || 0, false, Number(inv.adjusted) || 0);
    await finance.invoices.update(inv.id, { status, voidUndoneAt: now, voidUndoneBy: actor.id, voidUndoneByName: actor.name });
    await audit('invoice.unvoid', actor, inv.id, { status: 'void' }, { status, invoiceNo: inv.invoiceNo }, change.pastelFile ?? '');
  }
  await updateDoc(doc(db, RECON_CHANGES_COLLECTION, change.id), done);
}

export async function raiseReconInvoice(input: {
  student: { id: string; fullName: string; familyId?: string };
  amount: number;
  label: string;
  category: FeeCategory;
  term: TermKey;
  year: string;
  dueDate: string; // YYYY-MM-DD
  settings: FinanceSettings;
  /** Invoices already issued this year — the number sequence, as ManualInvoice counts it. */
  invoicesThisYear: number;
  actor: ReconActor;
  file: string;
}): Promise<Invoice> {
  const amt = round2(input.amount);
  if (!(amt > 0)) throw new Error('The amount must be more than nothing.');
  if (input.label.trim().length < 3) throw new Error('Say what the invoice is for.');
  const id = `${input.year}_${input.term}_${input.student.id}_r${Date.now().toString(36)}`;
  const invoiceNo = formatInvoiceNo(input.settings, { year: input.year, term: input.term, seq: input.invoicesThisYear + 1 });
  const inv = assembleInvoice({
    id, invoiceNo,
    studentId: input.student.id, studentName: input.student.fullName, familyId: input.student.familyId,
    term: input.term, year: input.year,
    issuedAt: new Date().toISOString(),
    dueDate: new Date(`${input.dueDate}T12:00:00`).toISOString(),
    lines: [{ label: input.label.trim(), category: input.category, amount: amt }],
    rules: [],
    status: 'issued',
  });
  // Every document carries kind (HIGH-RISK §1): an untagged one falls out of `!=` queries.
  await finance.invoices.set(id, { ...(inv as unknown as Record<string, unknown>), kind: 'invoice' });
  await audit('invoice.recon', input.actor, id, undefined, { studentId: input.student.id, invoiceNo, total: inv.total, emailed: false }, input.file);
  return inv;
}

/**
 * Bring a Pastel account the portal has nobody for into the portal, as
 * Pastel carries it: the skeleton the July load used for pending pupils
 * (name, Pastel code, no class, no gender — Admissions enrols them), or a
 * former pupil for an old account, plus an opening balance at Pastel's
 * figure. firestore.rules lets a finance manager create exactly this shape.
 */
export async function addPastelPupil(input: {
  name: string;
  code: string;
  status: 'pending' | 'left';
  balance: number;
  /** Opening documents numbered OPEN/2026/PC… so far — the sequence. */
  openingCount: number;
  actor: ReconActor;
  file: string;
}): Promise<{ studentId: string; invoiceId?: string; invoiceNo?: string }> {
  const name = input.name.trim().replace(/\s+/g, ' ');
  if (name.length < 3) throw new Error('The name is too short.');
  const code = input.code.trim().toUpperCase();
  const now = new Date().toISOString();
  const ref = doc(collection(db, 'reportStudents'));
  await setDoc(ref, {
    fullName: name, gender: '', currentClass: '', subjects: [], subjectTeachers: {},
    status: input.status, pastelCode: code,
    admissions: { notes: `Added from Pastel (${input.file}) — ${input.status === 'pending' ? 'pending enrolment' : 'former pupil, old account'}` },
    createdAt: now, createdBy: input.actor.id,
  });
  await audit('student.fromPastel', input.actor, ref.id, undefined, { fullName: name, pastelCode: code, status: input.status }, input.file);
  const balance = round2(input.balance);
  if (Math.abs(balance) < 0.005) return { studentId: ref.id };
  const invoiceId = `2026_Term2_${ref.id}_opening`;
  const invoiceNo = `OPEN/2026/PC${String(input.openingCount + 1).padStart(4, '0')}`;
  await finance.invoices.set(invoiceId, {
    id: invoiceId, invoiceNo, studentId: ref.id, studentName: name, term: 'Term2', year: '2026',
    issuedAt: now.slice(0, 10), dueDate: now.slice(0, 10),
    lines: [{ label: `Opening balance per Pastel ledger (${input.file})`, category: 'other', amount: balance }],
    discounts: [], subtotal: balance, discountTotal: 0, total: balance, paid: 0, balance,
    status: balance <= 0 ? 'paid' : 'issued', kind: 'opening', createdAt: now, updatedAt: now,
  });
  await audit('invoice.opening', input.actor, invoiceId, undefined, { studentId: ref.id, invoiceNo, total: balance }, input.file);
  return { studentId: ref.id, invoiceId, invoiceNo };
}

export async function linkPastelCode(studentId: string, code: string, actor: ReconActor, file: string): Promise<void> {
  const clean = code.trim().toUpperCase();
  if (!/^[A-Z0-9]{2,10}$/.test(clean)) throw new Error('That is not a Pastel customer code.');
  await updateDoc(doc(db, 'reportStudents', studentId), { pastelCode: clean, updatedAt: new Date().toISOString(), updatedBy: actor.id });
  await audit('student.pastelCode', actor, studentId, undefined, { pastelCode: clean }, file);
}

export async function renamePupil(studentId: string, from: string, to: string, actor: ReconActor, file: string): Promise<void> {
  const name = to.trim().replace(/\s+/g, ' ');
  if (name.length < 3) throw new Error('The name is too short.');
  await updateDoc(doc(db, 'reportStudents', studentId), { fullName: name, updatedAt: new Date().toISOString(), updatedBy: actor.id });
  await audit('student.rename', actor, studentId, { fullName: from }, { fullName: name }, file);
}

export interface ReconNote {
  key: string;
  note: string;
  /** The difference when it was noted: the card stays hidden while it holds. */
  difference: number;
  code?: string;
  studentId?: string | null;
  by: string;
  byName: string;
  at: string;
}

export function noteKey(row: { code?: string; studentId?: string | null }): string {
  return row.code ? `code_${row.code}` : `student_${row.studentId}`;
}

export async function noteDifference(row: { code?: string; studentId?: string | null; difference: number }, note: string, actor: ReconActor, file: string): Promise<void> {
  if (note.trim().length < 3) throw new Error('Say why the portal is right.');
  const key = noteKey(row);
  const rec: ReconNote = {
    key, note: note.trim(), difference: round2(row.difference), code: row.code || undefined, studentId: row.studentId ?? null,
    by: actor.id, byName: actor.name, at: new Date().toISOString(),
  };
  await setDoc(doc(db, RECON_NOTES_COLLECTION, key), rec as unknown as Record<string, unknown>);
  await audit('recon.note', actor, key, undefined, { note: rec.note, difference: rec.difference }, file);
}
